// STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · Canonical Common LIVE Operations hook.
//
// 목적: Studio (SellerLiveControl) 와 Console (SellerLiveConsole) 양쪽에서
// 동일한 active_product/Overlay/QR 조작을 서로 다른 inline 로직으로 중복
// 작성하지 않도록 공통 action 을 1곳에 집중한다.  shell 마다 UI 는 다를 수
// 있어도 server state 를 바꾸는 함수는 canonical 한 하나여야 한다.
//
// ─── Canonical contract (실제 backend route 기준) ──────────────────────────
//   · POST /seller/broadcasts/{sid}/announce       (단일 엔드포인트)
//        body: {
//          product_id: str | null,
//          preset?: 'default'|'full'|'price'|'options'|'option_price'|'size'
//                   |'mini'|'emphasized'|'hidden',
//          display?: { position?, size_table_visible?, inventory_mode? },
//          qr?:      { action: 'show'|'hide', duration_seconds? },
//        }
//   · POST /seller/broadcasts/{sid}/quick-product  (빠른 등록 전용)
//
// NOTE:
//   · /qr · /next-product · /prev-product 는 **백엔드에 존재하지 않음**.
//     SPLIT-1 초안 hook 이 가리키던 path 는 404 를 유발하므로 모두 제거.
//     QR 은 /announce body additive, next/prev 는 client-side walker 가
//     target product_id 를 계산해 announce(pid) 를 호출한다.
//   · active_product_id / active_product_display / active_product_qr 는
//     전부 live_schedules 1곳 SoT · build_display_state / build_qr_state 가
//     server-side 에서 담당.  이 hook 은 **payload shaping 책임만** 수행.
import { useCallback, useState } from 'react';
import api from '@/lib/api';

/**
 * useLiveOperations(scheduleId)
 *
 * Studio / Console shell 양쪽에서 import 하여 동일 action 을 호출.
 * busy state 는 action 단위 string ('announce' / 'preset' / 'position' /
 * 'size_table' / 'inventory_mode' / 'qr_show' / 'qr_hide' / 'quick' / 'goto').
 * 모든 action 은 실패 시 err 를 throw 하므로 caller 가 toast 처리.
 */
import { pickRelative } from '@/lib/liveRelative';

const useLiveOperations = (scheduleId) => {
  const [busy, setBusy] = useState(null);

  const _post = useCallback(async (path, body, key) => {
    if (!scheduleId) throw new Error('scheduleId 가 없습니다.');
    setBusy(key);
    try {
      const { data } = await api.post(`/seller/broadcasts/${scheduleId}${path}`, body || {});
      return data;
    } finally { setBusy(null); }
  }, [scheduleId]);

  // ─── active product · Overlay (canonical /announce) ───────────────────-

  // 상품 소개 + optional preset / display override.
  //   announce(pid)                                 → 활성 상품 변경 (preset 유지)
  //   announce(pid, 'full')                         → preset 교체
  //   announce(pid, 'default', { position: 'top' }) → display override
  const announce = useCallback((pid, preset, displayOverride) => {
    const body = {};
    body.product_id = pid ?? null;
    if (preset) body.preset = preset;
    if (displayOverride && typeof displayOverride === 'object') {
      body.display = displayOverride;
    }
    return _post('/announce', body, 'announce');
  }, [_post]);

  // 소개 해제 (active_product_id = null, QR 자동 hide · product_changed trigger).
  const clearAnnounce = useCallback(() => (
    _post('/announce', { product_id: null }, 'clear')
  ), [_post]);

  // Overlay preset 만 교체 (현재 active_product 유지 전제).
  const applyPreset = useCallback((pid, preset) => (
    _post('/announce', { product_id: pid, preset }, 'preset')
  ), [_post]);

  // 완전 숨김 preset.
  const hideOverlay = useCallback((pid) => (
    _post('/announce', { product_id: pid, preset: 'hidden' }, 'preset')
  ), [_post]);

  // Overlay 위치 (preset 보존 · position 만 override).
  const setPosition = useCallback((pid, currentPreset, pos) => (
    _post('/announce', {
      product_id: pid,
      preset: currentPreset,
      display: { position: pos },
    }, 'position')
  ), [_post]);

  // 사이즈표 Overlay 토글 (preset 보존).
  const toggleSizeTable = useCallback((pid, currentPreset, visible) => (
    _post('/announce', {
      product_id: pid,
      preset: currentPreset,
      display: { size_table_visible: !!visible },
    }, 'size_table')
  ), [_post]);

  // 재고 표시 모드 ('hidden' | 'exact' | 'status').  기존 두 shell 모두 preset 을
  // 함께 보내지 않았기 때문에 canonical 도 preset 생략 — build_display_state 가
  // seller_default 를 유지한다.
  const setInventoryMode = useCallback((pid, mode) => (
    _post('/announce', {
      product_id: pid,
      display: { inventory_mode: mode },
    }, 'inventory_mode')
  ), [_post]);

  // ─── QR Overlay (announce body additive · 별도 엔드포인트 없음) ─────────-

  const showQr = useCallback((pid, durationSec = 10) => (
    _post('/announce', {
      product_id: pid,
      qr: { action: 'show', duration_seconds: durationSec },
    }, 'qr_show')
  ), [_post]);

  const hideQr = useCallback((pid) => (
    _post('/announce', {
      product_id: pid,
      qr: { action: 'hide' },
    }, 'qr_hide')
  ), [_post]);

  // ─── next / prev (client-side walker · 백엔드 route 없음) ──────────────-

  // items: Array<{ product_id, sale_status? }>  (sort_order 로 미리 정렬된 상태)
  // currentPid: 현재 active_product_id (없으면 null)
  // delta: +1 (next) | -1 (prev)
  // 품절 상품은 자동 skip · 활성이 없으면 첫번째 판매가능 상품 소개.
  const gotoRelative = useCallback(async (items, currentPid, delta) => {
    if (!Array.isArray(items) || items.length === 0) {
      throw new Error('상품 목록이 비어 있습니다.');
    }
    const pid = pickRelative(items, currentPid, delta);
    if (pid) return announce(pid);
    throw new Error(delta > 0
      ? '다음 판매 가능한 상품이 없습니다.'
      : '이전 판매 가능한 상품이 없습니다.');
  }, [announce]);

  // ─── Quick register (STEP §5) ──────────────────────────────────────────-

  // body = { name, sale_price, image, stock?, options?, announce_now? }
  const quickRegister = useCallback((body) => (
    _post('/quick-product', body, 'quick')
  ), [_post]);

  return {
    busy,
    // canonical active product / overlay
    announce,
    clearAnnounce,
    applyPreset,
    hideOverlay,
    setPosition,
    toggleSizeTable,
    setInventoryMode,
    // QR (announce body additive)
    showQr,
    hideQr,
    // list navigation
    gotoRelative,
    // quick register
    quickRegister,
  };
};

export default useLiveOperations;
